import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import client from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const requiresCurrentPassword = !user?.must_change_password;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if ((requiresCurrentPassword && !currentPassword) || !newPassword || !confirmPassword) {
      setError('Bitte alle Felder ausfüllen');
      return;
    }
    if (newPassword.length < 12) {
      setError('Passwort muss mindestens 12 Zeichen lang sein');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);
    try {
      const response = await client.post('/api/auth/change-password', {
        ...(requiresCurrentPassword ? { currentPassword } : {}),
        newPassword,
      });
      setSuccess(response.data?.message || 'Passwort erfolgreich geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 5 }, py: { xs: 2, md: 2.5 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.65rem', sm: '2rem' } }}>
        {user?.must_change_password ? 'Passwortrichtlinie bestätigen' : 'Passwort ändern'}
      </Typography>

      <Card sx={{ maxWidth: 560 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            {user?.must_change_password && (
              <Alert severity="info">
                Sie haben sich bereits erfolgreich angemeldet. Legen Sie jetzt ein Passwort nach der neuen Richtlinie fest. Ihr bisheriges Passwort darf erneut verwendet werden, wenn es die Anforderungen erfüllt.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {requiresCurrentPassword && (
                  <TextField
                    fullWidth
                    label="Aktuelles Passwort"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={loading}
                  />
                )}
                <TextField
                  fullWidth
                  label={user?.must_change_password ? 'Passwort' : 'Neues Passwort'}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  helperText="12 bis 72 Zeichen; Passphrasen und Leerzeichen sind erlaubt"
                />
                <TextField
                  fullWidth
                  label="Neues Passwort wiederholen"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading ? 'Wird gespeichert...' : (user?.must_change_password ? 'Passwort bestätigen' : 'Passwort ändern')}
                </Button>
              </Stack>
            </form>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ChangePasswordPage;
